'use client';

import { useEffect } from 'react';

import usePublicEnvVars from '@/hooks/usePublicEnvVars';
import { disableFullRum, enableFullRum, initFaro } from '@/monitoring/client/faro';
import { useCookieConsent } from 'react-cookie-manager';

/**
 * FaroProvider — client component that initialises Grafana Faro once on mount.
 *
 * Renders nothing. Place it high in the component tree (inside Providers) so
 * that Faro is active before any user interaction or navigation occurs.
 *
 * `FARO_URL` and `FARO_APP_NAME` are read from PublicEnvVarsProvider, which
 * receives them from the server-side layout at runtime. This avoids the
 * NEXT_PUBLIC_ build-time constraint and lets a single Docker image run in all
 * environments with values sourced from Key Vault.
 *
 * Init is gated on FARO_URL being set, so this component is safe to include
 * unconditionally — it will silently no-op in environments where it is not
 * configured.
 */
const FaroProvider = () => {
  const publicEnv = usePublicEnvVars();
  const FARO_URL = publicEnv.PUBLIC_FARO_URL;
  const FARO_APP_NAME = 'Veni.Sourcing.Portal.UI';
  const RUNTIME_ENV = publicEnv.PUBLIC_RUNTIME_ENV;
  const { detailedConsent } = useCookieConsent();
  const hasAnalyticsConsent = detailedConsent?.Analytics.consented === true;

  useEffect(() => {
    initFaro(FARO_URL, FARO_APP_NAME, RUNTIME_ENV);
  }, [FARO_URL, RUNTIME_ENV]);

  useEffect(() => {
    if (hasAnalyticsConsent) enableFullRum();
    else if (detailedConsent) disableFullRum();
  }, [detailedConsent, hasAnalyticsConsent]);

  return null;
};

export default FaroProvider;
